import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Activity,
  Award,
  BookOpen,
  Lightbulb,
  Layers,
  LayoutDashboard,
  GraduationCap,
  Pin,
  PinOff,
  Moon,
  Sun,
} from "lucide-react";
import type { Theme } from "../hooks/useTheme";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useIsChampion } from "../hooks/useIsChampion";

interface NavItem {
  to: string;
  label: string;
  icon: typeof BookOpen;
  href?: string;
  /** Hidden from the nav until admin-gating is lifted for general release. */
  adminOnly?: boolean;
  /** Also shown to AI Champions (the Champion app role). */
  champions?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/dashboard-new", label: "Dashboard (New)", icon: LayoutDashboard, adminOnly: true, champions: true },
  { to: "/library", label: "AI Library", icon: BookOpen },
  { to: "/initiatives", label: "AI Initiatives", icon: Lightbulb },
  { to: "/platforms", label: "AI Platforms", icon: Layers },
  { to: "/training", label: "AI Training", icon: GraduationCap, href: "https://app.sectionai.com/platform" },
  { to: "/champions-new", label: "AI Champions (New)", icon: Award, adminOnly: true, champions: true },
  { to: "/activity", label: "Activity Log", icon: Activity, adminOnly: true },
];

type Props = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  theme: Theme;
  onToggleTheme: () => void;
  /** When true, render the inline collapse toggle (desktop). Hidden on mobile drawer. */
  showCollapseToggle?: boolean;
  /** Called when user taps a nav item (e.g. to close mobile drawer). */
  onNavigate?: () => void;
};

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  theme,
  onToggleTheme,
  showCollapseToggle = true,
  onNavigate,
}: Props) {
  // Hover-to-peek: when the user has chosen to keep the sidebar collapsed,
  // hovering over it temporarily expands it as an overlay so they can see
  // the labels without disturbing the page layout. Clicking a nav item
  // resets hover so the sidebar snaps back to its persisted collapsed state.
  const [hovered, setHovered] = useState(false);
  const effectiveCollapsed = collapsed && !hovered;
  const peeking = collapsed && hovered;
  const isAdmin = useIsAdmin();
  const isChampion = useIsChampion();
  const navItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin || (item.champions && isChampion));

  const handleNavigate = () => {
    setHovered(false);
    onNavigate?.();
  };

  return (
    <aside
      className={`sidebar${effectiveCollapsed ? " sidebar-collapsed" : ""}${
        peeking ? " sidebar-peek" : ""
      }`}
      aria-label="Primary navigation"
      onMouseEnter={() => {
        if (collapsed) setHovered(true);
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="sidebar-brand">
        <div className="sidebar-brand-mark" aria-hidden>
          <img src="/brookfield-ai.png" alt="" />
        </div>
        {!effectiveCollapsed ? (
          <span className="sidebar-brand-text">AI Navigator</span>
        ) : null}
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const Icon = item.icon;
          if (item.href) {
            return (
              <a
                key={item.to}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-nav-item"
                data-label={item.label}
                onClick={handleNavigate}
                title={effectiveCollapsed ? item.label : undefined}
              >
                <span className="sidebar-nav-icon" aria-hidden>
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                {!effectiveCollapsed ? (
                  <span className="sidebar-nav-label">{item.label}</span>
                ) : null}
              </a>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `sidebar-nav-item${isActive ? " is-active" : ""}`
              }
              data-label={item.label}
              onClick={handleNavigate}
              title={effectiveCollapsed ? item.label : undefined}
            >
              <span className="sidebar-nav-icon" aria-hidden>
                <Icon size={20} strokeWidth={1.75} />
              </span>
              {!effectiveCollapsed ? (
                <span className="sidebar-nav-label">{item.label}</span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>

      <div className="sidebar-foot">
        <button
          type="button"
          className="sidebar-icon-btn"
          onClick={onToggleTheme}
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        {showCollapseToggle ? (
          <button
            type="button"
            className="sidebar-icon-btn sidebar-collapse-btn"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Pin sidebar open" : "Unpin sidebar"}
            title={`${collapsed ? "Pin open" : "Unpin"} (Ctrl/Cmd+B)`}
          >
            {collapsed ? <PinOff size={18} /> : <Pin size={18} />}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
